/**
 * Tools API slices (tools, tool runs, categories, tags, studios).
 * Leaf module (Perf 3.1.b/e Step 5). Pulls only the shared chokepoint from
 * ./client; never imports a sibling @/lib/api slice.
 */
import { withManualAiSharingConsent } from '@/store/aiSharingConsent';
import { fetchWithAuth, ApiError } from './client';

async function allowExplicitToolRequest() {
  if (await withManualAiSharingConsent(() => true)) return;
  const error = new Error('AI data sharing was not allowed.');
  error.code = 'AI_SHARING_CONSENT_DECLINED';
  throw error;
}

export const toolsApi = {
  // List active tools. Filters: { category, studio, tag, featured, outputType, q, limit, page }
  list: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    });
    const qs = params.toString();
    return fetchWithAuth(`/json/tools${qs ? `?${qs}` : ''}`);
  },

  // Single tool by slug
  getBySlug: async (slug) => {
    return fetchWithAuth(`/json/tools/${encodeURIComponent(slug)}`);
  },

  // Tools related to this one (same category / shared tags / same outputType).
  // Drives the "Related tools" row at the bottom of the tool detail page.
  related: async (slug, { limit = 8 } = {}) => {
    const qs = new URLSearchParams({ limit: String(limit) }).toString();
    return fetchWithAuth(`/json/tools/${encodeURIComponent(slug)}/related?${qs}`);
  },

  // Prompt recipes for the same job as this tool (matched on intentAction /
  // outputType). Drives the "Related recipes" row on the tool detail page.
  relatedPrompts: async (slug, { limit = 8 } = {}) => {
    const qs = new URLSearchParams({ limit: String(limit) }).toString();
    return fetchWithAuth(`/json/tools/${encodeURIComponent(slug)}/related-prompts?${qs}`);
  },

  // Run a tool. inputs is the user's submitted form values (matching tool.fields names).
  // Optional overrides: { modelId, modelIds, operation, conversationTitle, previousRunId }
  // For tools with `flags.supportsMultipleModels` you should send `modelIds`
  // (the full ordered pick set); single-model tools can keep using `modelId`.
  // The backend accepts both — if both are passed, `modelIds` wins.
  run: async (slug, { inputs, modelId, modelIds, operation, conversationTitle, previousRunId } = {}) => {
    await allowExplicitToolRequest();
    return fetchWithAuth(`/json/tools/${encodeURIComponent(slug)}/run`, {
      method: 'POST',
      body: { inputs: inputs || {}, modelId, modelIds, operation, conversationTitle, previousRunId },
    });
  },

  // Fire-and-forget view beacon. Bumps tool.stats.views server-side. Safe to
  // ignore the response — the caller doesn't need to wait for it.
  trackView: async (slug) => {
    return fetchWithAuth(`/json/tools/${encodeURIComponent(slug)}/view`, {
      method: 'POST',
    });
  },

  // Magic prompt suggestion for a single form field. Powers the small wand
  // button next to prompt-style inputs. `contextValues` should be the rest of
  // the form (so the suggestion can respect the user's other selections like
  // tone, genre, length, etc).
  enhanceField: async (slug, { fieldName, currentValue = '', contextValues = {} } = {}) => {
    await allowExplicitToolRequest();
    return fetchWithAuth(`/json/tools/${encodeURIComponent(slug)}/enhance-field`, {
      method: 'POST',
      body: { fieldName, currentValue, contextValues },
    });
  },

  // Estimate the credit cost of running this tool with the given form state.
  // Mirrors conversationsApi.calculateCredits: returns the same { cost,
  // userCredits } shape, sourced from the real CreditCalculationManager so
  // the displayed number matches what the user will actually be charged.
  calculateCredits: async (slug, { inputs = {}, modelId = null, modelIds = null, fileIds = [] } = {}) => {
    return fetchWithAuth(`/json/tools/${encodeURIComponent(slug)}/calculate-credits`, {
      method: 'POST',
      body: { inputs, modelId, modelIds, fileIds },
    });
  },

  favoriteTool: async (toolId) => {
    const data = await fetchWithAuth(`/json/user/me/favorite-tools/${encodeURIComponent(toolId)}`, {
      method: 'POST',
    });
    if (data.error) throw new ApiError(data.message || 'Failed to favorite tool', data.status || 400, data);
    return !data.error;
  },

  unfavoriteTool: async (toolId) => {
    const data = await fetchWithAuth(`/json/user/me/favorite-tools/${encodeURIComponent(toolId)}`, {
      method: 'DELETE',
    });
    if (data.error) throw new ApiError(data.message || 'Failed to unfavorite tool', data.status || 400, data);
    return !data.error;
  },
};

export const toolRunsApi = {
  // List runs. Filters: { toolSlug, status, limit, before }
  list: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    });
    const qs = params.toString();
    return fetchWithAuth(`/json/tool-runs${qs ? `?${qs}` : ''}`);
  },

  getById: async (id) => {
    return fetchWithAuth(`/json/tool-runs/${encodeURIComponent(id)}`);
  },

  // Actively polls the run's pending InferenceJobs server-side, then returns
  // the refreshed run. Use this in the in-flight progress loop instead of
  // `getById` so the run actually progresses in environments where the
  // background polling worker isn't running. Server caches/throttles internally.
  poll: async (id) => {
    return fetchWithAuth(`/json/tool-runs/${encodeURIComponent(id)}/poll`);
  },

  // Promotes a completed tool run into a fresh, user-visible Conversation.
  // Returns { conversation: { id, title } }.
  promoteToConversation: async (id) => {
    return fetchWithAuth(`/json/tool-runs/${encodeURIComponent(id)}/promote-to-conversation`, {
      method: 'POST',
    });
  },

  // Soft-delete a tool run (also tears down the hidden backing conversation).
  delete: async (id) => {
    return fetchWithAuth(`/json/tool-runs/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },
};

export const toolCategoriesApi = {
  list: async () => fetchWithAuth('/json/tool-categories'),
};

export const toolTagsApi = {
  list: async ({ featured } = {}) => {
    const qs = featured === true ? '?featured=true' : '';
    return fetchWithAuth(`/json/tool-tags${qs}`);
  },
};

export const toolStudiosApi = {
  list: async () => fetchWithAuth('/json/tool-studios'),
};
